/**
 * Pure helpers for the persisted purchase-order UI (Phase 3b). Types off the generated client + a
 * status → label/tone map so the list badges render consistently. No IO.
 */
import type { components } from '@/lib/api/types.gen';

export type PurchaseOrderSummary = components['schemas']['PurchaseOrderSummary'];
export type PurchaseOrderView = components['schemas']['PurchaseOrderView'];
export type PurchaseOrderStatus = PurchaseOrderSummary['status'];

/** Human label + a coarse tone for each lifecycle status (drives the list badge). */
export function poStatusMeta(status: PurchaseOrderStatus): {
  label: string;
  tone: 'pending' | 'active' | 'done' | 'void';
} {
  switch (status) {
    case 'QUEUED':
      return { label: 'Not yet ordered', tone: 'pending' };
    case 'ORDERED':
      return { label: 'Ordered', tone: 'active' };
    case 'RECEIVING':
      return { label: 'Receiving', tone: 'active' };
    case 'CLOSED':
      return { label: 'Received', tone: 'done' };
    case 'CANCELED':
      return { label: 'Canceled', tone: 'void' };
  }
}

/** An OPEN PO (its units still net the reorder signal). */
export function isOpenPo(status: PurchaseOrderStatus): boolean {
  return status === 'QUEUED' || status === 'ORDERED' || status === 'RECEIVING';
}

/**
 * A prior live-order attempt whose outcome is not settled — an order MAY exist at S&S. Blocks a
 * resend (server-side too) and drives the "check the S&S portal" badge.
 */
export function hasUnresolvedAttempt(po: PurchaseOrderSummary): boolean {
  return po.ssOrderAttemptStatus === 'in_flight' || po.ssOrderAttemptStatus === 'unknown';
}

/**
 * Can a LIVE, BILLABLE S&S order be placed for this PO? QUEUED with no unreconciled prior attempt —
 * mirrors the server gate.
 *
 * ⚠️ ONE predicate with TWO readers: the row's button and the lifted confirm dialog. The confirm used
 * to live inside the row, so the row's own visibility rules hid it by construction; now that it is a
 * page-level dialog it must re-check the same rule against the live list, or a background refetch
 * could leave a billable confirm standing over a PO the row would already have refused.
 */
export function isPlaceable(po: PurchaseOrderSummary): boolean {
  return po.status === 'QUEUED' && !hasUnresolvedAttempt(po);
}

/** Can this PO be voided? Same statuses the server accepts for /cancel. */
export function isCancelable(po: PurchaseOrderSummary): boolean {
  return po.status === 'QUEUED' || po.status === 'ORDERED';
}
